import { Injectable } from '@angular/core';
import { FormArray, FormGroup, Validators } from '@angular/forms';
import { UserDto } from '@bo-client/shared/models/userDto';
import { GisDataModel } from '@bo-client/shared/models/gisData';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import {
  FeedInManagementInfoEnum,
  GridRegistrationTypeEnum
} from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import { FeederContractModel } from '@bo-client/activation-management/feeder-contract/model/feeder-contract.model';
import { BankAccountModel } from '@bo-client/shared/models/bankAccountModel';
import { DataShareService } from '@bo-client/shared/services/data-share.service';

@Injectable({
  providedIn: 'root'
})
export class LoadDataFormsGridRegistrationService {
  constructor(public dataShareService: DataShareService) {}

  loadCommonData(model: any, selectedGisData: GisDataModel, currentForm: FormGroup): any {
    if (selectedGisData) {
      if (!model.connectionObject) {
        model.connectionObject = {};
      }
      model.connectionObject.street = selectedGisData.street;
      model.connectionObject.streetNo = selectedGisData.houseNo;
      model.connectionObject.streetNoAddition = selectedGisData.houseNoAddition;
      model.connectionObject.zipCode = selectedGisData.zipCode;
      model.connectionObject.city = selectedGisData.city;
      model.connectionObject.sapLocationId = selectedGisData.sapDistrictNumber;
      model.connectionObject.district = selectedGisData.district;
      model.connectionObject.developmentAreaName = selectedGisData.developmentAreaName;
      model.connectionObject.corridorNumber = selectedGisData.corridorNumber;
      model.uniqueCoId = selectedGisData.uniqueCoId;
    }
    // additional Object Info
    model.connectionObjectAdditionalInfo.isNewBuilding = currentForm
      .get('additionalInfoConnectionObject')
      .get('isNewBuilding').value;
    model.connectionObjectAdditionalInfo.buildingType = currentForm
      .get('additionalInfoConnectionObject')
      .get('buildingType').value;
    model.connectionObjectAdditionalInfo.numberOfFlats = currentForm
      .get('additionalInfoConnectionObject')
      .get('numberOfFlats').value;
    model.connectionObjectAdditionalInfo.numberOfUsers = currentForm
      .get('additionalInfoConnectionObject')
      .get('numberOfUsers').value;
    model.connectionObjectAdditionalInfo.numberOfOtherUsers = currentForm
      .get('additionalInfoConnectionObject')
      .get('numberOfOtherUsers').value;
    model.connectionObjectAdditionalInfo.objectUsage = currentForm
      .get('additionalInfoConnectionObject')
      .get('objectUsage').value;

    const listFiliation: any = (currentForm.get('filiationData') as FormArray).value;
    model.customer = listFiliation[0];
    if (listFiliation.length > 1) {
      model.hasDifferentInvoiceContact = true;
      model.invoiceContact = listFiliation[1];
    } else {
      model.invoiceContact = null;
    }
    model.producers = currentForm.get('producers') ? (currentForm.get('producers') as FormArray).value : [];
    model.storages = currentForm.get('storages') ? (currentForm.get('storages') as FormArray).value : [];
    model.pvModules = currentForm.get('pvModules') ? (currentForm.get('pvModules') as FormArray).value : [];
    model.uploads = (currentForm.get('uploads') as FormArray).value.filter((ele) => ele.url);

    model.feedingPlantType = currentForm.get('feedingPlantType').value;
    model.powerGenerationSource = currentForm.get('powerGenerationSource').value;
    model.isEEGExtention = currentForm.get('isEEGExtention').value;
    model.feedInManagementInfo = currentForm.get('feedInManagementInfo').value as FeedInManagementInfoEnum;
    model.comment = currentForm.get('comment').value;
    if (
      currentForm.get('extractionNetworkPowerAuthorized') &&
      currentForm.get('extractionNetworkPowerAuthorized').value
    ) {
      model.extractionNetworkPowerAuthorized = currentForm.get('extractionNetworkPowerAuthorized').value;
    }
    if (currentForm.get('hasInverterInstalled')) {
      model.hasInverterInstalled = currentForm.get('hasInverterInstalled').value;
    }
    return model;
  }

  loadGROtherInfoExternalUsers(
    currentGridReg: GridRegistrationModel,
    selectedCompanyLocation: CompanyLocationDto,
    currentForm: FormGroup
  ): GridRegistrationModel {
    currentGridReg.companyLocation = {
      id: selectedCompanyLocation ? selectedCompanyLocation.id : null
    };
    currentGridReg.existingFeeders = currentForm.get('existingFeeders')
      ? (currentForm.get('existingFeeders') as FormArray).value
      : [];
    currentGridReg.consumers = currentForm.get('consumers')
      ? (currentForm.get('consumers') as FormArray).value
      : [];

    currentGridReg.meterNumber = currentForm.get('meterNumber')?.value;
    return currentGridReg;
  }

  loadGridRegDataToSave(
    userDto: UserDto,
    currentGridReg: GridRegistrationModel,
    selectedCompanyLocation: CompanyLocationDto,
    currentForm: FormGroup
  ): GridRegistrationModel {
    currentGridReg.companyLocation = {
      id: selectedCompanyLocation ? selectedCompanyLocation.id : null
    };
    if (userDto) {
      currentGridReg.technician = { id: userDto.id };
    }
    if (currentGridReg.feederContract) {
      currentGridReg.feederContract = { id: currentGridReg.feederContract.id };
    }
    currentGridReg.contractedConnectionPower = currentForm.get('contractedConnectionPower').value;
    currentGridReg.connectionFuseAmplitude = currentForm.get('connectionFuseAmplitude').value;
    currentGridReg.shortDescription = currentForm.get('shortDescription').value;
    currentGridReg.longDescription = currentForm.get('longDescription')?.value;
    currentGridReg.externalId = currentForm.get('externalId').value;
    currentGridReg.externalCustomerId = currentForm.get('externalCustomerId')?.value;
    currentGridReg.assignedTransformerStation = currentForm.get('assignedTransformerStation').value;
    currentGridReg.meterNumber = currentForm.get('meterNumber').value
      ? currentForm.get('meterNumber').value
      : null;

    if (!currentGridReg.placeInService) {
      currentGridReg.placeInService = {};
    }
    currentGridReg.placeInService.placeInServiceDatum = currentForm.get('placeInServiceDatum').value
      ? currentForm.get('placeInServiceDatum').value
      : null;
    currentGridReg.consumptionOver30kw = currentForm.get('consumptionOver30kw').value
      ? currentForm.get('consumptionOver30kw').value
      : null;
    currentGridReg.existingFeeders = currentForm.get('existingFeeders')
      ? (currentForm.get('existingFeeders') as FormArray).value
      : [];
    currentGridReg.consumers = currentForm.get('consumers')
      ? (currentForm.get('consumers') as FormArray).value
      : [];
    currentGridReg.hasInverterInstalled = currentForm.get('hasInverterInstalled').value;
    return currentGridReg;
  }

  loadFeederContractToSave(
    model: FeederContractModel,
    companyLocationId: number,
    currentForm: FormGroup
  ): FeederContractModel {
    model.companyLocation = { id: companyLocationId };
    model.gridRegistration = { id: model.gridRegistration.id };
    model = this.getFeederContractConfirmationDataFromForm(model, currentForm);
    return model;
  }

  loadFeederContractConfirmContractByCustomer(
    model: FeederContractModel,
    currentForm: FormGroup
  ): FeederContractModel {
    return this.getFeederContractConfirmationDataFromForm(model, currentForm);
  }

  getFeederContractConfirmationDataFromForm(
    model: FeederContractModel,
    currentForm: FormGroup
  ): FeederContractModel {
    model.commissioningDate = currentForm.get('commissioningDate')
      ? currentForm.get('commissioningDate').value
      : null;
    model.plantOperatorAndEndUserAreIdentical = currentForm.get('plantOperatorAndEndUserAreIdentical')
      ? currentForm.get('plantOperatorAndEndUserAreIdentical').value
      : null;
    model.maStRNumberProducer = currentForm.get('maStRNumberProducer')
      ? currentForm.get('maStRNumberProducer').value
      : null;
    model.maStRNumberStorage = currentForm.get('maStRNumberStorage')
      ? currentForm.get('maStRNumberStorage').value
      : null;
    model.deviceNo = currentForm.get('deviceNo') ? currentForm.get('deviceNo').value : null;
    model.maStRNumber = currentForm.get('maStRNumber') ? currentForm.get('maStRNumber').value : null;
    model.maStRAcceptedDeclaration = currentForm.get('maStRAcceptedDeclaration')
      ? currentForm.get('maStRAcceptedDeclaration').value
      : null;
    model.maStRRegistrationDate = currentForm.get('maStRRegistrationDate')
      ? currentForm.get('maStRRegistrationDate').value
      : null;

    /**
     * und auch noch für die neuen FeldeR:
     *  // New fields
     *       maStRSELNumber: [data.maStRSELNumber], // MaStR Nr. der Stromerzeugungslokation der Einheit !
     *       maStREEGNumber: [data.maStREEGNumber], // MaStR Nr. der Erzeugungsanlage (EEG Nummer) !
     *       maStRSSENumberStorage: [data.maStRSSENumberStorage], // MaStR SSE des Speichers !
     *       maStREEGNumberStorage: [data.maStREEGNumberStorage], // MaStR EEG der Erzeugungsanlage (SEE Nummer) !
     *
     *       batteryTechnology: [data.batteryTechnology], // Batterietechnologie
     *       usageType: [data.usageType], // MaStR Nutzungsart
     *       orientation: [data.orientation], // Ausrichtung
     *       tilt: [data.tilt], // Neigung
     *       secondaryOrientation: [data.secondaryOrientation], // Nebenausrichtung
     *       secondaryTilt: [data.secondaryTilt], // Neigung Nebenausrichtung
     */
    model.maStRSELNumber = currentForm.get('maStRSELNumber')?.value;
    model.maStREEGNumber = currentForm.get('maStREEGNumber')?.value;
    model.maStRSSENumberStorage = currentForm.get('maStRSSENumberStorage')?.value;
    model.maStREEGNumberStorage = currentForm.get('maStREEGNumberStorage')?.value;
    model.batteryTechnology = currentForm.get('batteryTechnology')?.value;
    model.batteryType = currentForm.get('batteryType')?.value;
    model.usageType = currentForm.get('usageType')?.value;
    model.orientation = currentForm.get('orientation')?.value;
    model.tilt = currentForm.get('tilt')?.value;
    model.secondaryOrientation = currentForm.get('secondaryOrientation')?.value;
    model.secondaryTilt = currentForm.get('secondaryTilt')?.value;

    model.meterOperator = currentForm.get('meterOperator') ? currentForm.get('meterOperator').value : null;
    model.ustPflichtig = currentForm.get('ustPflichtig') ? currentForm.get('ustPflichtig').value : null;
    model.ustNr = currentForm.get('ustNr') ? currentForm.get('ustNr').value : null;
    model.taxNr = currentForm.get('taxNr') ? currentForm.get('taxNr').value : null;
    model.steuerArt = currentForm.get('steuerArt') ? currentForm.get('steuerArt').value : null;
    model.invoiceContact = currentForm.get('invoiceContact')
      ? currentForm.get('invoiceContact').value[0]
      : null;
    if (currentForm.get('shortDescription')) {
      model.shortDescription = currentForm.get('shortDescription').value;
    }
    if (currentForm.get('longDescription')) {
      model.longDescription = currentForm.get('longDescription').value;
    }
    if (currentForm.get('externalId')) {
      model.externalId = currentForm.get('externalId').value;
    }
    if (currentForm.get('externalCustomerId')) {
      model.externalId = currentForm.get('externalCustomerId').value;
    }

    if (currentForm.get('assignedTransformerStation')) {
      model.assignedTransformerStation = currentForm.get('assignedTransformerStation').value;
    }

    if (currentForm.get('externalGridRegistrationId')) {
      model.externalGridRegistrationId = currentForm.get('externalGridRegistrationId').value;
    }

    if (currentForm.get('bankAccount') && currentForm.get('bankAccount').get('iban')) {
      if (!model.bankAccount) {
        model.bankAccount = new BankAccountModel();
      }
      model.bankAccount.iban = currentForm.get('bankAccount').get('iban')
        ? currentForm.get('bankAccount').get('iban').value
        : null;
      model.bankAccount.bic = currentForm.get('bankAccount').get('bic')
        ? currentForm.get('bankAccount').get('bic').value
        : null;
      model.bankAccount.holder = currentForm.get('bankAccount').get('holder')
        ? currentForm.get('bankAccount').get('holder').value
        : null;
      model.bankAccount.bankName = currentForm.get('bankAccount').get('bankName')
        ? currentForm.get('bankAccount').get('bankName').value
        : null;
    }
    return model;
  }

  setAcceptConditionsDisable(currentForm: FormGroup): FormGroup {
    // currentForm.get('acceptConditions').get('installationCorrectlyExecuted').patchValue(true);
    // currentForm.get('acceptConditions').get('installationCorrectlyExecuted').disable();
    currentForm.get('acceptConditions').get('ownerConsent').patchValue(true);
    currentForm.get('acceptConditions').get('ownerConsent').disable();
    return currentForm;
  }

  commonFormActionsAfterSaveCommissioning(currentForm: FormGroup): FormGroup {
    // currentForm.get('acceptConditions').get('installationCorrectlyExecuted').disable();
    currentForm.get('acceptConditions').get('ownerConsent').disable();
    return currentForm;
  }

  // Todo: refactor this nested conditions
  resetConnectionObjectAdditionalInfo(
    currentGR: GridRegistrationModel,
    currentForm: FormGroup
  ): GridRegistrationModel {
    if (currentForm.get('additionalInfoConnectionObject')) {
      if (currentForm.get('additionalInfoConnectionObject').get('objectType')) {
        switch (currentForm.get('additionalInfoConnectionObject').get('objectType').value) {
          case 'SingleHouse':
            currentGR.connectionObjectAdditionalInfo.numberOfFlats = 0;
            currentGR.connectionObjectAdditionalInfo.numberOfUsers = 0;
            currentGR.connectionObjectAdditionalInfo.numberOfOtherUsers = 0;
            currentGR.connectionObjectAdditionalInfo.objectUsage = '';
            break;
          case 'MultiHouse':
            currentGR.connectionObjectAdditionalInfo.numberOfOtherUsers = 0;
            currentGR.connectionObjectAdditionalInfo.objectUsage = '';
            break;
          case 'NonResidential':
            currentGR.connectionObjectAdditionalInfo.numberOfFlats = 0;
            currentGR.connectionObjectAdditionalInfo.numberOfUsers = 0;
            break;
        }
      }
    }
    return currentGR;
  }

  setValidatorsFeedInManagementInfo(
    totalPowerPVModules: number,
    totalPowerProducers: number,
    gridRegistrationType: GridRegistrationTypeEnum,
    $event: { loadForm: any },
    gRForm: FormGroup
  ): FormGroup {
    console.log('SETTING VALIDATORS FEED IN MANAGEMENT INFO', $event);
    if ($event.loadForm) {
      // WARUM IST DAS NOTWENDIG?
      if (gRForm.get('feedInManagementInfo').value) {
        gRForm.get('feedInManagementInfo').patchValue(gRForm.get('feedInManagementInfo').value);
      }
    } else {
      /**
       * Sonderfall für PLuginProducer. Diese brauchen in keinem Fall ein EInspeisermanagement
       * Leistungsgrenzen werden anderer Stelle validiert.
       */
      if (gridRegistrationType === GridRegistrationTypeEnum.PlugInProducer) {
        gRForm.get('feedInManagementInfo').clearValidators();
        gRForm.get('feedInManagementInfo').patchValue(null);
        gRForm.get('feedInManagementInfo').markAsTouched();
        return gRForm;
      }

      gRForm.get('feedInManagementInfo').patchValue(null);
      gRForm.get('feedInManagementInfo').setValidators([Validators.required]);
      gRForm.get('feedInManagementInfo').setValidators([Validators.required]);
      gRForm.get('feedInManagementInfo').markAsTouched();
    }
    return gRForm;
  }

  setFeedInManagementInfoNotRequired($event, gRForm: FormGroup): FormGroup {
    if ($event.loadForm) {
      if (gRForm.get('feedInManagementInfo').value) {
        gRForm.get('feedInManagementInfo').patchValue(gRForm.get('feedInManagementInfo').value);
      }
    } else {
      gRForm.get('feedInManagementInfo').clearValidators();
      gRForm.get('feedInManagementInfo').patchValue(null);
      gRForm.get('feedInManagementInfo').markAsTouched();
    }
    return gRForm;
  }
}
